import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { Guide } from '../../types/guide';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Upload, AlertTriangle, ExternalLink } from 'lucide-react';
import { uploadFile } from '../../utils/firebase';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  guide: Guide;
  onUpdate: (guideId: string, updates: Partial<Guide>) => Promise<void>;
};

export const EditGuideInfoModal = ({ isOpen, onClose, guide, onUpdate }: Props) => {
  const { translations } = useLanguage();
  const [formData, setFormData] = useState({
    name: guide.name || '',
    passportNumber: guide.passportNumber || '',
    nusukEmail: guide.nusukEmail || guide.email || '',
    mainPhone: guide.mainPhone || '',
    hajjPhone: guide.hajjPhone || ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // New state for passport photo upload
  const [passportFile, setPassportFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = translations?.editGuideModal?.nameRequired || 'Guide name is required';
    }

    // Validate phone numbers if provided
    if (formData.mainPhone) {
      const phoneRegex = /^\+[1-9]\d{1,14}$/; // Basic E.164 format: +country_code followed by digits
      if (!phoneRegex.test(formData.mainPhone)) {
        newErrors.mainPhone = translations?.editGuideModal?.phoneFormatError || 'Phone must include country code (e.g., +966XXXXXXXX)';
      }
    }

    // Specifically validate Hajj phone to be a Saudi number
    if (formData.hajjPhone) {
      if (!formData.hajjPhone.startsWith('+966')) {
        newErrors.hajjPhone = 'Hajj phone must be a Saudi number starting with +966';
      } else if (!/^\+966\d{9}$/.test(formData.hajjPhone)) {
        newErrors.hajjPhone = 'Invalid Saudi number format (should be +966 followed by 9 digits)';
      }
    }

    // Validate nusuk email if provided
    if (formData.nusukEmail && !/\S+@\S+\.\S+/.test(formData.nusukEmail)) {
      newErrors.nusukEmail = translations?.editGuideModal?.emailFormatError || 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      // Check if passport number exists first
      if (!formData.passportNumber) {
        toast.error('Passport number is required before uploading passport photo');
        e.target.value = ''; // Reset file input
        return;
      }
      
      const file = files[0];
      setPassportFile(file);
      
      // Validate file type and size
      const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!validTypes.includes(file.type)) {
        setFileError('File must be JPG, PNG, or PDF');
      } else if (file.size > maxSize) {
        setFileError('File size must be less than 5MB');
      } else {
        setFileError('');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || fileError) {
      return;
    }
    
    setIsLoading(true);
    try {
      let passportPhotoUrl = guide.passportPhotoUrl;

      // Upload passport photo if provided
      if (passportFile) {
        setIsUploading(true);
        
        // Create a new file with passport number as name but keep original extension
        const fileExtension = passportFile.name.split('.').pop();
        const renamedFile = new File(
          [passportFile],
          `passport_${formData.passportNumber}.${fileExtension}`,
          { type: passportFile.type }
        );
        
        const result = await uploadFile(renamedFile);
        passportPhotoUrl = result.url;
        setIsUploading(false);
      }

      await onUpdate(guide.id, {
        name: formData.name,
        passportNumber: formData.passportNumber || null, // Changed from undefined to null
        nusukEmail: formData.nusukEmail || null, // Changed from undefined to null
        mainPhone: formData.mainPhone || null, // Changed from undefined to null
        hajjPhone: formData.hajjPhone || null, // Changed from undefined to null
        passportPhotoUrl // Add passport photo URL
      });
      
      toast.success(translations?.editGuideModal?.success || 'Guide information updated successfully');
      onClose();
    } catch (error: any) {
      console.error('Error updating guide:', error);
      toast.error(error.message || translations?.editGuideModal?.error || 'Failed to update guide information');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to extract passport number from URL
  const getFilenameFromUrl = (url: string) => {
    try {
      // Extract filename from path
      const pathSegments = new URL(url).pathname.split('/');
      const filename = pathSegments[pathSegments.length - 1];
      
      // Try to extract passport number from the filename
      // Pattern: passport_PASSPORTNUMBER.extension
      const match = filename.match(/passport_([^.]+)\.(.+)$/);
      if (match && match.length >= 3) {
        return `${match[1]}.${match[2]}`;
      }
      
      // If pattern doesn't match, just return the filename
      return filename;
    } catch (error) {
      return 'passport-photo';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={translations?.editGuideModal?.title || `Edit Guide: ${guide.name}`}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {translations?.editGuideModal?.nameLabel || 'Guide Name'}
            <span className="text-red-500 mr-1">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-500">{errors.name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {translations?.editGuideModal?.passportLabel || 'Passport Number'} <span className="text-xs text-gray-500">({translations?.common?.optional || 'optional'})</span>
          </label>
          <input
            type="text"
            value={formData.passportNumber}
            onChange={(e) => setFormData({ ...formData, passportNumber: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Passport Photo Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Passport Photo <span className="text-xs text-gray-500">({translations?.common?.optional || 'optional'}, JPG, PNG, or PDF, max 5MB)</span>
          </label>
          <div className={`border-2 border-dashed rounded-lg p-4 text-center ${
            fileError ? 'border-red-300 bg-red-50' : 
            passportFile ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-primary-300'
          }`}>
            {passportFile ? (
              <div className="space-y-2">
                <div className="text-sm font-medium text-green-700">{passportFile.name}</div>
                <div className="text-xs text-green-600">
                  {(passportFile.size / 1024 / 1024).toFixed(2)} MB
                </div>
                <button 
                  type="button"
                  onClick={() => {
                    setPassportFile(null);
                    setFileError('');
                  }}
                  className="text-xs text-red-500 hover:text-red-700"
                >
                  Remove file
                </button>
              </div>
            ) : (
              <>
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-2">
                  <label 
                    htmlFor="passport-file-upload" 
                    className={`cursor-pointer ${!formData.passportNumber ? 'text-gray-400 pointer-events-none' : 'text-primary-600 hover:text-primary-500'}`}
                    title={!formData.passportNumber ? 'Passport number is required before uploading photo' : 'Click to upload passport photo'}
                  >
                    Click to upload
                  </label>{' '}
                  or drag and drop
                </div>
                <p className="text-xs text-gray-500 mt-1">JPG, PNG, or PDF up to 5MB</p>
                {!formData.passportNumber && (
                  <p className="text-xs text-amber-500 mt-1">Passport number is required before uploading</p>
                )}
                <input
                  id="passport-file-upload"
                  type="file"
                  className="sr-only"
                  accept="image/jpeg,image/png,application/pdf"
                  onChange={handleFileChange}
                  disabled={!formData.passportNumber}
                />
              </>
            )}
          </div>
          {fileError && (
            <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
              <AlertTriangle size={14} /> {fileError}
            </p>
          )}
          {guide.passportPhotoUrl && !passportFile && (
            <div className="mt-2 text-sm text-gray-600">
              Current file: {getFilenameFromUrl(guide.passportPhotoUrl)}
              <a 
                href={guide.passportPhotoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-1 text-primary-600 hover:text-primary-500 flex items-center gap-1 mt-1"
              >
                <ExternalLink size={12} />
                <span>View passport photo</span>
              </a>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {translations?.editGuideModal?.emailLabel || 'Nusuk Email'} <span className="text-xs text-gray-500">({translations?.common?.optional || 'optional'})</span>
          </label>
          <input
            type="email"
            value={formData.nusukEmail}
            onChange={(e) => setFormData({ ...formData, nusukEmail: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
              errors.nusukEmail ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.nusukEmail && (
            <p className="mt-1 text-sm text-red-500">{errors.nusukEmail}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {translations?.editGuideModal?.mainPhoneLabel || 'Main Phone Number'} <span className="text-xs text-gray-500">({translations?.common?.optionalCountryCode || 'optional, include country code +xxx'})</span>
          </label>
          <input
            type="tel"
            placeholder="+966XXXXXXXXX"
            value={formData.mainPhone}
            onChange={(e) => setFormData({ ...formData, mainPhone: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
              errors.mainPhone ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.mainPhone && (
            <p className="mt-1 text-sm text-red-500">{errors.mainPhone}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {translations?.editGuideModal?.hajjPhoneLabel || 'Hajj Phone Number'} <span className="text-xs text-gray-500">(must be a Saudi number +966)</span>
          </label>
          <input
            type="tel"
            placeholder="+966XXXXXXXXX"
            value={formData.hajjPhone}
            onChange={(e) => setFormData({ ...formData, hajjPhone: e.target.value })}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
              errors.hajjPhone ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.hajjPhone && (
            <p className="mt-1 text-sm text-red-500">{errors.hajjPhone}</p>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.common?.cancel || 'Cancel'}
          </button>
          
          <motion.button
            type="submit"
            disabled={isLoading || isUploading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg text-white ${
              isLoading || isUploading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary-500 hover:bg-primary-600'
            }`}
          >
            {isLoading || isUploading
              ? (translations?.common?.saving || 'Saving...')
              : (translations?.common?.saveChanges || 'Save Changes')}
          </motion.button>
        </div>
      </form>
    </Modal>
  );
};